// Canonical SMRT COP site progression. Each step, once completed, records
// { at, by, byName } and advances the site's DISPLAYED status to that step's
// `status` label. The internal COPSite.status enum is unchanged (it still
// drives gating); the progression status here is a display layer derived from
// the recorded history, which lives on `site.history`.

export interface SiteHistoryEntry {
  at: string; // ISO timestamp
  by?: string; // actor user id (auth sub / userID)
  byName?: string; // actor display name
}

export type SiteHistoryStepKey =
  | "siteAdded"
  | "detailsVerified"
  | "crewAssigned"
  | "towerComplete"
  | "tapeDropSent"
  | "photosSent"
  | "bomSent"
  | "retSent"
  | "alignmentSent"
  | "rg123Sent"
  | "clientApproved";

export type SiteHistory = Partial<
  Record<SiteHistoryStepKey, SiteHistoryEntry | null>
>;

export interface SiteHistoryStep {
  key: SiteHistoryStepKey;
  label: string; // shown in the history timeline
  status: string; // displayed site status once this step is the latest done
}

// Order matters — this is the progression. RET and Tape Drop are sent by admin
// BEFORE tower work is marked complete; Photos and BOM can only be sent once the
// tower crew has finished, so they follow "Tower work Complete".
export const SITE_HISTORY_STEPS: SiteHistoryStep[] = [
  { key: "siteAdded", label: "Site Added", status: "Verify Site Details" },
  { key: "detailsVerified", label: "Site Details Verified", status: "Assign Crew" },
  { key: "crewAssigned", label: "Crew Assigned", status: "Send RET" },
  { key: "retSent", label: "RET Report Sent to Client", status: "Send Tape Drop" },
  { key: "tapeDropSent", label: "Tape Drop sent to Client", status: "Tower In Progress" },
  { key: "towerComplete", label: "Tower work Complete", status: "Send Photos" },
  { key: "photosSent", label: "Site Photos sent to Client", status: "Send BOM" },
  { key: "bomSent", label: "BOM Sent to Client", status: "Send Alignment" },
  { key: "alignmentSent", label: "Alignment Report Sent to Client", status: "Send RG123" },
  { key: "rg123Sent", label: "RG123 Sent to Client", status: "pending client approval" },
  // Recorded server-side when the "Confirm Client Approval" SMRT Task — auto-
  // assigned to every cop.admin the moment RG123 is sent — is marked Complete.
  // Reaching this step LOCKS the site (see isSiteLocked in routes/smrtcop.ts);
  // an admin can undo it via Reopen Approval, which clears this entry.
  { key: "clientApproved", label: "Client Approved", status: "Client Approved" },
];

// Displayed status = the `status` of the latest completed step (the next
// action the site is waiting on). Defaults to the first step's status.
export function deriveProgressStatus(history: SiteHistory | undefined): string {
  let status = SITE_HISTORY_STEPS[0].status;
  if (!history) return status;
  for (const step of SITE_HISTORY_STEPS) {
    if (history[step.key]) status = step.status;
  }
  return status;
}

// Build a timeline (done + meta) for the history modal.
export function buildSiteTimeline(
  history: SiteHistory | undefined,
): Array<{ key: string; label: string; done: boolean; at?: string; byName?: string }> {
  return SITE_HISTORY_STEPS.map((step) => {
    const entry = history?.[step.key];
    return {
      key: step.key,
      label: step.label,
      done: !!entry,
      at: entry?.at,
      byName: entry?.byName,
    };
  });
}
